import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { useAllergyHealthParameter } from '@/modules/app/hooks/useAllergyHealthParameter'
import { AllergyList } from '@/modules/shared/components/allergy-list/AllergyList'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatLocale } from '@/modules/shared/helpers/locale'

import type { AllergyProfile } from '@/modules/app/types/HealthParameter.types'

type Props = {
  showDescriptions?: boolean
}

export const AllergiesHealthParameterSection = ({ showDescriptions = true }: Props) => {
  const theme = useTheme()
  const {
    data: allergyHealthProfile,
    isLoading: allergyResourceIsLoading,
    error: allergyResourceError,
  } = useAllergyHealthParameter()

  // Template variables
  // ------------------------------------------------------------------------- /

  const healthParameter = allergyHealthProfile?.healthParameters?.[0]
  const allergyProfile = healthParameter?.observations?.[0]?.value as AllergyProfile | undefined

  const visibleAllergies = allergyProfile?.allergies?.filter((a) => a.diffStatus !== 'deleted') ?? []

  const infomativeIntro = t({
    id: 'health-parameter.allergies.intro',
    comment: 'Informative intro for allergies data',
    message:
      'Bij je volgende afspraak in het ziekenhuis zullen we je vragen ons je actuele lijst van allergieën te bezorgen.',
  })

  // Render
  // ------------------------------------------------------------------------- /

  if (allergyResourceIsLoading) {
    return (
      <FlexColumn alignItems='center' justifyContent='center'>
        <AnimatedLoader small={true} />
      </FlexColumn>
    )
  }

  if (allergyResourceError) {
    return (
      <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
        {t({
          id: 'error.general',
          message: 'Er is een fout opgetreden',
        })}
      </Span>
    )
  }

  return (
    <FlexColumn gap={theme.UI.SpacingPx.Space6}>
      {showDescriptions && infomativeIntro ? (
        <FlexColumn gap={theme.UI.SpacingPx.Space2}>
          <Span type='BodyMediumBold'>{infomativeIntro}</Span>
        </FlexColumn>
      ) : null}

      {showDescriptions && healthParameter?.lastUpdatedAt ? (
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'health-parameter.allergies.lastUpdatedAt',
            message: 'Ingegeven op {date}',
            values: {
              date: formatLocale(new Date(healthParameter.lastUpdatedAt), 'dd MMMM yyyy'),
            },
          })}
        </Span>
      ) : null}

      <AllergyList allergies={visibleAllergies} schemeStatus={allergyProfile?.schemeStatus} />
    </FlexColumn>
  )
}
